import type {Wallet} from '@stripe-internal/wallet-connect-wrapper';
import type {NetworkType, SupportedCurrency} from 'src/v2/state/types';
import {EVM_NETWORKS} from 'src/types';

/**
 * Maps a wallet to its network type based on the network it is connected to.
 * Every supported non-Solana network (ethereum, polygon, base, tempo) is EVM.
 */
export const getWalletNetworkType = (wallet: Wallet): NetworkType =>
  EVM_NETWORKS.includes(wallet.network) ? 'evm' : 'solana';

/**
 * Derives the set of network types a token can be paid on, based on the
 * WalletConnect-supported currencies. Tokens like USDC can span both EVM and
 * Solana, whereas ETH is EVM-only and SOL is Solana-only.
 */
export const getTokenNetworkTypes = (
  token: SupportedCurrency['assetCode'],
  walletConnectSupportedCurrencies: SupportedCurrency[],
): Set<NetworkType> => {
  const networkTypes = new Set<NetworkType>();

  walletConnectSupportedCurrencies
    .filter((currency) => currency.assetCode === token)
    .forEach((currency) => {
      networkTypes.add(
        EVM_NETWORKS.includes(currency.currencyNetwork) ? 'evm' : 'solana',
      );
    });

  return networkTypes;
};

/**
 * Finds a connected wallet whose network type can pay the token. Prefers the
 * currently active wallet when it is already compatible, otherwise falls back
 * to the first compatible connected wallet. Returns null when no connected
 * wallet can pay the token.
 */
export const findCompatibleWallet = (
  activeWallet: Wallet | null,
  connectedWallets: Wallet[],
  tokenNetworkTypes: Set<NetworkType>,
): Wallet | null => {
  const isCompatible = (wallet: Wallet): boolean =>
    tokenNetworkTypes.has(getWalletNetworkType(wallet));

  if (activeWallet && isCompatible(activeWallet)) {
    return activeWallet;
  }

  return connectedWallets.find(isCompatible) ?? null;
};
